import { Inject, Injectable, isDevMode } from '@angular/core';
import { CONFIG_TOKEN, StorageToken } from 'core/constants';
import { CimaToken } from './token';
import { Util } from '../util';
import { SessionStorage } from '../storage/session';
import { IXMOptions } from 'core/interfaces';

@Injectable({
    providedIn: 'root'
})
export class CimaUrl {
    private cimaToken: CimaToken;
    private config: IXMOptions;
    private sessionStorage: SessionStorage;

    constructor(cimaToken: CimaToken, sessionStorage: SessionStorage, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { cimaToken, sessionStorage, config });
    }

    public redirectToLogin(returnState: SavedRouteState, additionalPath: string = ''): void {
        this.sessionStorage.set(StorageToken.SAVED_STATE, JSON.stringify(returnState));

        window.location.assign(this.loginString(additionalPath));
    }

    public loginString(additionalPath: string = ''): string {
        const queryParams: string = Util.createQueryParams({
            client_id: this.config.CLIENT_ID,
            redirect_uri: this.browserRedirectUri(additionalPath),
            response_type: 'code'
        });

        if (this.config.ENABLE_COMMERCIAL_LOGIN_DIRECTLY || Util.hasCBMSSO() || window.location.href.includes('acc=')) {
            return `${this.config.CIMA_BASE_URL}/oauth/authorize${queryParams}&realm=commercial`;
        }

        const cableContextId: string = Util.fetchCableContextId();
        if (cableContextId) {
            return `${this.config.CIMA_BASE_URL}/oauth/authorize${queryParams}&id_token_hint=${cableContextId}`;
        }

        return `${this.config.CIMA_BASE_URL}/oauth/authorize${queryParams}`;
    }

    public redirectToLogout(returnURI: string = ''): void {
        this.cimaToken.logoutCleanup();

        window.location.assign(this.logoutString(returnURI));
    }

    public logoutString(returnURI: string = ''): string {
        const params: CimaLogout = {
            client_id: this.config.CLIENT_ID
        };

        // only if coming from an xfinity domain will we set a state
        if (!isDevMode() && window.location.origin && window.location.origin.includes('xfinity.com')) {
            params.state = encodeURIComponent(`${window.location.origin}${this.config.BASE_URL}${returnURI}`);
        }

        const queryParams: string = Util.createQueryParams(params);

        return `${this.config.CIMA_BASE_URL}/oauth/sp-logout${queryParams}`;
    }

    public browserRedirectUri(additionalPath: string = ''): string {
        const endUrlPath: string = `${window.location.origin}${this.config.BASE_URL}${additionalPath ? additionalPath + '/' : ''}login`;

        if (this.config.CIMA_RETURN_URL) {
           return encodeURIComponent(`${this.config.CIMA_RETURN_URL}?state=${endUrlPath}`);
        } else {
            return encodeURIComponent(endUrlPath);
        }
    }

    public apiRedirectUri(isPassiveFlow: boolean = false): string {
        if (isPassiveFlow) {
            return `${window.location.origin}${this.config.BASE_URL}cima.html`;
        } else if (this.config.CIMA_RETURN_URL) {
            return `${this.config.CIMA_RETURN_URL}?state=${window.location.href.split('?')[0]}`;
        } else {
            return window.location.href.split('?')[0];
        }
    }

    public cimaAuthLogin(returnState: SavedRouteState, baseURL: string, additionalPath: string = ''): void {
        this.sessionStorage.set(StorageToken.SAVED_STATE, JSON.stringify(returnState));

        window.location.assign(this.cimaAuthLoginString(baseURL, additionalPath));
    }

    public cimaAuthLoginString(baseURL: string, additionalPath: string = ''): string {
        const queryParams: string = Util.createQueryParams({
            client_id: this.config.CLIENT_ID,
            redirect_uri: this.browserRedirectUri(additionalPath),
            response_type: 'code'
        }, '&');

        return `${baseURL}${queryParams}`;
    }
}
